import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { galleryApi } from '../services/gallery';
import { FullscreenViewer } from '../components/common/FullscreenViewer';
import { CommentsSection } from '../components/common/CommentsSection';
import { TagsDisplay } from '../components/common/TagsDisplay';

export const AlbumPage = () => {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [viewMode, setViewMode] = useState('grid');
  const [currentIndex, setCurrentIndex] = useState(0);

  const [fullscreenIndex, setFullscreenIndex] = useState(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Состояния для редактирования альбома
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', data_time: '', description: '' });
  const [isSaving, setIsSaving] = useState(false);

  const [commentsRef] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    galleryApi.getAlbum(albumId, user?.token)
      .then(res => {
        setData(res);
        setCurrentIndex(0);
      })
      .catch(err => {
        console.error('❌ Ошибка загрузки альбома:', err);
        navigate('/');
      })
      .finally(() => setLoading(false));
  }, [albumId, user?.token, navigate]);

  const handleNext = useCallback(() => {
    if (data && currentIndex < data.photos.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [data, currentIndex]);

  const handlePrev = useCallback(() => {
    if (data && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [data, currentIndex]);

  const openFullscreen = (index) => setFullscreenIndex(index);
  const closeFullscreen = () => setFullscreenIndex(null);

  const handleFsNext = () => {
    if (data && fullscreenIndex < data.photos.length - 1) {
      setFullscreenIndex(prev => prev + 1);
    }
  };

  const handleFsPrev = () => {
    if (data && fullscreenIndex > 0) {
      setFullscreenIndex(prev => prev - 1);
    }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (fullscreenIndex !== null) {
        if (e.key === 'Escape') closeFullscreen();
        else if (e.key === 'ArrowRight') handleFsNext();
        else if (e.key === 'ArrowLeft') handleFsPrev();
        return;
      }

      if (viewMode === 'carousel') {
        if (e.key === 'ArrowRight') handleNext();
        else if (e.key === 'ArrowLeft') handlePrev();
        else if (e.key === 'Escape') navigate('/#gallery');
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [fullscreenIndex, viewMode, handleNext, handlePrev, handleFsNext, handleFsPrev, navigate]);

  const handleDeleteAlbum = async () => {
    if (!user || !albumId) return;
    setIsDeleting(true);
    try {
      await galleryApi.deleteAlbum(albumId, user.token);
      navigate('/#gallery');
    } catch (err) {
      alert('Ошибка: ' + err.message);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDownload = async (photo) => {
    if (!photo) return;
    try {
      const res = await fetch(photo.url);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${photo.title || `photo_${currentIndex + 1}`}.jpg`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(photo.url, '_blank');
    }
  };

  // Открытие модального окна редактирования
  const openEditModal = () => {
    if (!data) return;
    setEditForm({
      title: data.title || '',
      data_time: data.year || data.data_time || '',
      description: data.description || ''
    });
    setShowEditModal(true);
  };

  // Сохранение изменений альбома
  const handleSaveAlbum = async () => {
    if (!user?.is_admin || !albumId) return;

    setIsSaving(true);
    try {
      await galleryApi.updateAlbum(
        albumId,
        {
          title: editForm.title,
          data_time: editForm.data_time,
          description: editForm.description
        },
        user.token
      );

      setShowEditModal(false);

      // Обновляем данные альбома
      const updatedData = await galleryApi.getAlbum(albumId, user.token);
      setData(updatedData);
    } catch (err) {
      alert('Ошибка сохранения: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoUpdate = useCallback(() => {
    galleryApi.getAlbum(albumId, user?.token)
      .then(res => setData(res))
      .catch(err => console.error('Ошибка обновления:', err));
  }, [albumId, user?.token]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-dark)', color: 'var(--text-muted)' }}>
        <span className="reveal visible">Загрузка альбома...</span>
      </div>
    );
  }

  if (!data) return null;

  const currentPhoto = data.photos[currentIndex];
  const fullScreenPhoto = fullscreenIndex !== null ? data.photos[fullscreenIndex] : null;

  return (
    <div className="album-page" style={{ minHeight: '100vh', background: 'var(--bg-dark)', color: 'var(--text-light)', padding: '80px 40px 60px' }}>

      {/* === ВЕРХНЯЯ ПАНЕЛЬ === */}
      <div style={{ marginBottom: '40px', borderBottom: '1px solid rgba(201, 169, 110, 0.2)', paddingBottom: '20px', position: 'relative' }}>
        <button
          onClick={() => navigate('/#gallery')}
          style={{
            position: 'fixed', top: '30px', left: '30px',
            background: 'rgba(201, 169, 110, 0.1)',
            border: '1px solid var(--accent)',
            color: 'var(--accent)',
            padding: '10px 20px',
            cursor: 'pointer',
            fontFamily: 'Special Elite, monospace',
            fontSize: '0.85rem',
            letterSpacing: '2px',
            borderRadius: '4px',
            zIndex: 100,
            transition: 'all 0.3s'
          }}
          onMouseEnter={e => e.target.style.background = 'var(--accent)'}
          onMouseLeave={e => { e.target.style.background = 'rgba(201, 169, 110, 0.1)'; e.target.style.color = 'var(--accent)'; }}
        >
          ← Назад в галерею
        </button>

        <div style={{ marginTop: '40px' }}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: '300', marginBottom: '10px', color: 'var(--text-light)', fontFamily: 'Cormorant Garamond, serif' }}>{data.title}</h1>
          <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: '20px' }}>
            {data.description || 'Архивный материал'} • {data.photos.length} фото
          </p>

          {/* 🔥 Отображение тегов */}
          {data.tags && data.tags.length > 0 && (
            <TagsDisplay tags={data.tags} />
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
            {/* 🔥 Кнопки управления для админа */}
            {user?.is_admin && (
              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <button
                  onClick={openEditModal}
                  style={{
                    background: 'rgba(201, 169, 110, 0.15)',
                    border: '1px solid rgba(201, 169, 110, 0.4)',
                    color: 'var(--accent)',
                    padding: '8px 16px',
                    fontFamily: 'Special Elite, monospace',
                    fontSize: '0.85rem',
                    letterSpacing: '1px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    transition: 'all 0.25s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={e => e.target.style.background = 'rgba(201, 169, 110, 0.25)'}
                  onMouseLeave={e => e.target.style.background = 'rgba(201, 169, 110, 0.15)'}
                >
                  ✏️ Редактировать альбом
                </button>

                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                  style={{
                    background: 'rgba(231, 76, 60, 0.1)',
                    border: '1px solid rgba(231, 76, 60, 0.4)',
                    color: '#e74c3c',
                    padding: '8px 16px',
                    fontFamily: 'Special Elite, monospace',
                    fontSize: '0.85rem',
                    letterSpacing: '1px',
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    borderRadius: '4px',
                    transition: 'all 0.25s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={e => e.target.style.background = 'rgba(231, 76, 60, 0.2)'}
                  onMouseLeave={e => e.target.style.background = 'rgba(231, 76, 60, 0.1)'}
                >
                  🗑 Удалить альбом
                </button>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <button
                onClick={() => setViewMode('grid')}
                style={{
                  background: viewMode === 'grid' ? 'var(--accent)' : 'rgba(201, 169, 110, 0.08)',
                  color: viewMode === 'grid' ? 'var(--bg-dark)' : 'var(--text-muted)',
                  border: '1px solid rgba(201, 169, 110, 0.3)',
                  padding: '8px 16px',
                  fontFamily: 'Special Elite, monospace',
                  fontSize: '0.75rem',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  transition: 'all 0.25s'
                }}
              >
                ⊞ Сетка
              </button>
              <button
                onClick={() => setViewMode('carousel')}
                style={{
                  background: viewMode === 'carousel' ? 'var(--accent)' : 'rgba(201, 169, 110, 0.08)',
                  color: viewMode === 'carousel' ? 'var(--bg-dark)' : 'var(--text-muted)',
                  border: '1px solid rgba(201, 169, 110, 0.3)',
                  padding: '8px 16px',
                  fontFamily: 'Special Elite, monospace',
                  fontSize: '0.75rem',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  transition: 'all 0.25s'
                }}
              >
                ◈ Карусель
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* === СЕТКА === */}
      {viewMode === 'grid' && (
        <div className="album-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '20px' }}>
          {data.photos.map((photo, i) => (
            <div
              key={photo.pk_photo}
              onClick={() => { setViewMode('carousel'); setCurrentIndex(i); }}
              style={{
                position: 'relative',
                aspectRatio: '1',
                overflow: 'hidden',
                border: '1px solid rgba(201, 169, 110, 0.2)',
                cursor: 'pointer',
                transition: 'all 0.3s',
                backgroundColor: '#1a1a1a'
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'scale(1.02)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(201, 169, 110, 0.2)'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <img src={photo.url} alt={photo.title || `Фото ${i + 1}`} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(0.2) sepia(0.1)' }} />
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, padding: '15px',
                background: 'linear-gradient(to top, rgba(10,10,8,0.9), transparent)',
                display: 'flex', flexDirection: 'column', gap: '5px'
              }}>
                <span style={{ fontFamily: 'Special Elite, monospace', fontSize: '0.7rem', color: 'var(--accent)', letterSpacing: '2px' }}>{String(i + 1).padStart(2, '0')}</span>
                {photo.title && <span style={{ fontSize: '0.85rem', color: 'var(--text-light)', lineHeight: '1.3' }}>{photo.title}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* === КАРУСЕЛЬ === */}
      {viewMode === 'carousel' && currentPhoto && (
        <>
          <div style={{ position: 'relative', minHeight: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: '1000px', margin: '0 auto' }}>

            <button onClick={handlePrev} disabled={currentIndex === 0} style={{ position: 'absolute', left: '-60px', background: 'rgba(10,10,8,0.8)', border: '1px solid rgba(201,169,110,0.3)', width: '50px', height: '50px', cursor: currentIndex === 0 ? 'not-allowed' : 'pointer', opacity: currentIndex === 0 ? '0.2' : '1', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: 'var(--text-light)' }}>‹</button>

            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'Special Elite, monospace', fontSize: '1rem', color: 'var(--accent)', letterSpacing: '4px', marginBottom: '15px' }}>
                {String(currentIndex + 1).padStart(2, '0')} / {String(data.photos.length).padStart(2, '0')}
              </div>

              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={currentPhoto.url}
                  alt={currentPhoto.title || 'Фото'}
                  onClick={() => openFullscreen(currentIndex)}
                  style={{
                    maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain',
                    cursor: 'zoom-in', filter: 'contrast(1.05) sepia(0.1)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.5)', borderRadius: '4px'
                  }}
                />
                <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '8px' }}>
                  <button onClick={() => handleDownload(currentPhoto)} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', width: '35px', height: '35px', borderRadius: '50%', cursor: 'pointer', fontSize: '1rem' }}>⬇</button>
                </div>
              </div>

              {currentPhoto.title && <div style={{ marginTop: '15px', color: 'var(--text-muted)', fontStyle: 'italic' }}>{currentPhoto.title}</div>}
            </div>

            <button onClick={handleNext} disabled={currentIndex === data.photos.length - 1} style={{ position: 'absolute', right: '-60px', background: 'rgba(10,10,8,0.8)', border: '1px solid rgba(201,169,110,0.3)', width: '50px', height: '50px', cursor: currentIndex === data.photos.length - 1 ? 'not-allowed' : 'pointer', opacity: currentIndex === data.photos.length - 1 ? '0.2' : '1', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: 'var(--text-light)' }}>›</button>
          </div>

          {/* Секция комментариев */}
          <div style={{
            maxWidth: '900px',
            margin: '60px auto 0',
            padding: '0 20px'
          }}>
            <CommentsSection
              key={currentPhoto.pk_photo}
              photoId={currentPhoto.pk_photo}
              photoData={currentPhoto}
              onPhotoUpdate={handlePhotoUpdate}
            />
          </div>
        </>
      )}

      {/* === FULLSCREEN VIEWER === */}
      {fullScreenPhoto && (
        <FullscreenViewer
          key={fullScreenPhoto.url}
          imageUrl={fullScreenPhoto.url}
          title={fullScreenPhoto.title}
          index={fullscreenIndex}
          total={data.photos.length}
          onNext={handleFsNext}
          onPrev={handleFsPrev}
          onClose={closeFullscreen}
        />
      )}

      {/* 🔥 МОДАЛЬНОЕ ОКНО РЕДАКТИРОВАНИЯ АЛЬБОМА */}
      {showEditModal && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(5, 5, 3, 0.9)',
          zIndex: 100000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(5px)'
        }} onClick={() => setShowEditModal(false)}>
          <div style={{
            background: 'linear-gradient(160deg, #1c1c16 0%, #12120e 100%)',
            border: '1.5px solid rgba(201, 169, 110, 0.6)',
            borderRadius: '16px',
            padding: '35px 40px',
            maxWidth: '500px',
            width: '90vw',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.7)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: '1.6rem',
              color: 'var(--accent)',
              marginBottom: '25px',
              margin: '0 0 25px 0'
            }}>
              ✏️ Редактировать альбом
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '8px', fontFamily: 'Special Elite, monospace' }}>
                Название альбома
              </label>
              <input
                type="text"
                value={editForm.title}
                onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                placeholder="Введите название альбома"
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(201, 169, 110, 0.3)',
                  borderRadius: '8px',
                  color: 'var(--text-light)',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '8px', fontFamily: 'Special Elite, monospace' }}>
                Дата (ГГГГ-ММ-ДД)
              </label>
              <input
                type="date"
                value={editForm.data_time}
                onChange={e => setEditForm({ ...editForm, data_time: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(201, 169, 110, 0.3)',
                  borderRadius: '8px',
                  color: 'var(--text-light)',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '8px', fontFamily: 'Special Elite, monospace' }}>
                Описание
              </label>
              <textarea
                value={editForm.description}
                onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Краткое описание альбома"
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(201, 169, 110, 0.3)',
                  borderRadius: '8px',
                  color: 'var(--text-light)',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'var(--text-muted)',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Special Elite, monospace',
                  fontSize: '0.9rem'
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleSaveAlbum}
                disabled={isSaving}
                style={{
                  background: 'var(--accent)',
                  border: 'none',
                  color: 'var(--bg-dark)',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  fontFamily: 'Special Elite, monospace',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  opacity: isSaving ? 0.6 : 1
                }}
              >
                {isSaving ? 'Сохранение...' : '💾 Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === МОДАЛКА УДАЛЕНИЯ === */}
      {showDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(5, 5, 3, 0.9)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-medium)', border: '2px solid rgba(231, 76, 60, 0.5)', padding: '35px', textAlign: 'center', maxWidth: '420px', borderRadius: '10px', boxShadow: '0 25px 80px rgba(0, 0, 0, 0.8)' }}>
            <h3 style={{ color: '#e74c3c', marginBottom: '10px', fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem' }}>Удалить альбом целиком?</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '20px' }}>
              Вы уверены, что хотите удалить альбом "{data.title}" и все {data.photos.length} фотографий? Это действие необратимо.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={handleDeleteAlbum} disabled={isDeleting} style={{ background: '#e74c3c', color: 'white', border: 'none', padding: '10px 20px', cursor: isDeleting ? 'not-allowed' : 'pointer', fontFamily: 'Special Elite, monospace', borderRadius: '4px' }}>
                {isDeleting ? 'Удаление...' : 'Да, удалить'}
              </button>
              <button onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting} style={{ background: 'transparent', border: '1px solid var(--text-muted)', color: 'var(--text-muted)', padding: '10px 20px', cursor: isDeleting ? 'not-allowed' : 'pointer', fontFamily: 'Special Elite, monospace', borderRadius: '4px' }}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};